<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="720"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Import from listing</span>
        <v-chip
          class="ml-3"
          x-small
          label
          color="warning"
          text-color="white"
        >
          Beta
        </v-chip>
        <v-spacer />
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-4">
          Paste your Airbnb or VRBO listing URL to start a draft guidebook. You
          can edit the preview before creating. Add WiFi, lock codes, and host
          contact details after import.
        </p>

        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <v-alert
          v-if="step === 'done'"
          type="success"
          dense
          outlined
          class="mb-4"
        >
          Draft guidebook created. Open the editor to finish private details,
          then publish when ready.
        </v-alert>

        <template v-if="step === 'url' || step === 'paste'">
          <v-text-field
            v-model="url"
            label="Airbnb or VRBO listing URL"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
            :disabled="loading"
            placeholder="https://www.airbnb.com/rooms/..."
          />

          <v-alert
            v-if="step === 'paste'"
            type="info"
            dense
            outlined
            class="mb-4"
          >
            {{ pasteMessage }}
          </v-alert>

          <v-textarea
            v-if="step === 'paste'"
            v-model="pastedText"
            label="Paste listing description, amenities, and house rules"
            outlined
            dense
            rows="8"
            auto-grow
            hide-details="auto"
            class="mb-2"
            :disabled="loading"
          />
        </template>

        <template v-if="step === 'preview' && fields">
          <v-chip small class="mb-3" color="primary" outlined>
            {{ fields.importPlatform || platform }}
          </v-chip>

          <v-text-field
            v-model="fields.name"
            label="Property name"
            outlined
            dense
            counter="26"
            maxlength="26"
            class="mb-2"
            :disabled="loading"
          />

          <v-select
            v-model="fields.type"
            :items="propertyTypes"
            label="Property type"
            outlined
            dense
            class="mb-2"
            :disabled="loading"
          />

          <v-text-field
            v-model="fields.address"
            label="Street address"
            outlined
            dense
            class="mb-2"
            :disabled="loading"
          />

          <div class="d-flex flex-wrap">
            <v-text-field
              v-model="fields.city"
              label="City"
              outlined
              dense
              class="mr-2 mb-2"
              style="flex: 1 1 140px"
              :disabled="loading"
            />
            <v-text-field
              v-model="fields.state"
              label="State"
              outlined
              dense
              class="mr-2 mb-2"
              style="flex: 0 1 100px"
              :disabled="loading"
            />
            <v-text-field
              v-model="fields.zip"
              label="Zip"
              outlined
              dense
              class="mb-2"
              style="flex: 0 1 100px"
              :disabled="loading"
            />
          </div>

          <v-expansion-panels flat class="mb-2">
            <v-expansion-panel
              v-for="section in previewSections"
              :key="section.key"
            >
              <v-expansion-panel-header>
                {{ section.label }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-textarea
                  v-model="fields[section.key]"
                  outlined
                  dense
                  rows="4"
                  auto-grow
                  hide-details
                  :disabled="loading"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-alert type="warning" dense text class="mb-0">
            Still needed after import: WiFi password, lock/check-in code, host
            phone, trash instructions.
          </v-alert>
        </template>

        <template v-if="step === 'done'">
          <p class="text-body-2 mb-2">
            Property ID: <strong>{{ createdPropertyId }}</strong>
          </p>
          <p class="text-caption text--secondary mb-0">
            Remember to publish when the guidebook is ready for guests.
          </p>
        </template>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn text :disabled="loading" @click="close">
          {{ step === "done" ? "Close" : "Cancel" }}
        </v-btn>
        <v-spacer />
        <ehc-btn
          v-if="step === 'url' || step === 'paste'"
          color="primary"
          dark
          :loading="loading"
          :disabled="!canPreview"
          @click="runPreview"
        >
          {{ step === "paste" ? "Generate preview" : "Continue" }}
        </ehc-btn>
        <ehc-btn
          v-if="step === 'preview'"
          outlined
          class="mr-2"
          :disabled="loading"
          @click="backToUrl"
        >
          Back
        </ehc-btn>
        <ehc-btn
          v-if="step === 'preview'"
          color="primary"
          dark
          :loading="loading"
          :disabled="!fields || !fields.name"
          @click="runCreate"
        >
          Create draft guidebook
        </ehc-btn>
        <ehc-btn
          v-if="step === 'done'"
          color="primary"
          dark
          @click="openEditor"
        >
          Open editor
        </ehc-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiMixin from "@/mixins/api.vue";

export default {
  name: "EhcListingImport",
  mixins: [apiMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orgId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      step: "url",
      url: "",
      pastedText: "",
      pasteMessage: "",
      platform: null,
      fields: null,
      importedAt: null,
      loading: false,
      error: null,
      createdPropertyId: null,
      propertyTypes: [
        "house",
        "apartment",
        "hotel",
        "resort",
        "hostel",
        "camp",
        "camper",
        "hospital",
        "hospitalBrazil",
        "other",
      ],
      previewSections: [
        { key: "description", label: "Welcome / description" },
        { key: "amenities", label: "Amenities" },
        { key: "checkin", label: "Check-in" },
        { key: "parking", label: "Parking" },
        { key: "houserules", label: "House rules" },
        { key: "howto", label: "How-to" },
        { key: "popular", label: "Popular places" },
        { key: "ourfavorites", label: "Favorites" },
      ],
    };
  },
  computed: {
    canPreview() {
      if (!this.url.trim()) {
        return false;
      }
      if (this.step === "paste" && !this.pastedText.trim()) {
        return false;
      }
      return true;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
      }
    },
  },
  methods: {
    reset() {
      this.step = "url";
      this.url = "";
      this.pastedText = "";
      this.pasteMessage = "";
      this.platform = null;
      this.fields = null;
      this.importedAt = null;
      this.loading = false;
      this.error = null;
      this.createdPropertyId = null;
    },
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
    backToUrl() {
      this.step = this.pastedText ? "paste" : "url";
      this.error = null;
    },
    functionErrorMessage(error) {
      return (
        error?.message ||
        error?.details ||
        "Something went wrong. Please try again."
      );
    },
    async runPreview() {
      this.loading = true;
      this.error = null;
      try {
        const result = await this.apiPreviewListingImport({
          orgId: this.orgId,
          url: this.url.trim(),
          pastedText: this.pastedText.trim(),
        });

        if (result.status === "needsPasteText") {
          this.step = "paste";
          this.platform = result.platform;
          this.pasteMessage =
            result.message ||
            "Could not read the listing page. Paste listing details below.";
          return;
        }

        if (result.status !== "ready" || !result.fields) {
          throw new Error("Unexpected preview response.");
        }

        this.fields = { ...result.fields };
        this.platform = result.platform || result.importPlatform;
        this.importedAt = result.importedAt || Date.now();
        this.step = "preview";
      } catch (error) {
        console.error("runPreview failed", error);
        this.error = this.functionErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    async runCreate() {
      this.loading = true;
      this.error = null;
      try {
        const result = await this.apiCreatePropertyFromListing({
          orgId: this.orgId,
          fields: this.fields,
          importSourceUrl:
            this.fields.importSourceUrl || this.url.trim(),
          importPlatform:
            this.fields.importPlatform || this.platform,
          importedAt: this.importedAt || Date.now(),
        });

        this.createdPropertyId = result.propertyId;
        this.step = "done";
        this.$emit("created", {
          propertyId: result.propertyId,
          importPlatform: result.importPlatform,
        });
      } catch (error) {
        console.error("runCreate failed", error);
        this.error = this.functionErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    openEditor() {
      if (!this.createdPropertyId) {
        return;
      }
      const url = `${this.$store.getters.appSite}login/${this.createdPropertyId}`;
      window.open(url, "", "width=900, height=812");
      this.close();
    },
  },
};
</script>
